import { isFromApp } from "@/utils/utils";
import { useEffect, useState } from "react";
import { IResult, UAParser } from "ua-parser-js";

export const useDeviceDetails = () => {
  const [userAgentInfo, setUserAgentInfo] = useState<null | IResult>(null);
  const [appDeviceInfo, setAppDeviceInfo] = useState<{
    manufacturer?: string;
    model?: string;
    os?: string;
    browser?: string;
  } | null>(null);

  useEffect(() => {
    let nativeResolved = false;

    if (isFromApp()) {
      try {
        // ✅ iOS
        if (typeof (window as any).webkit?.messageHandlers?.getDeviceInfo?.postMessage === 'function') {
          (window as any).webkit.messageHandlers.getDeviceInfo.postMessage({});
        }

        // ✅ Android
        else if (typeof (window as any).JSBridge?.getDeviceInfo === 'function') {
          const deviceInfo = (window as any).JSBridge.getDeviceInfo();
          if (deviceInfo) {
            const parsed = typeof deviceInfo === 'string' ? JSON.parse(deviceInfo) : deviceInfo;
            nativeResolved = true;
            setAppDeviceInfo(parsed);
          }
        }
      } catch (error) {
        console.warn('Failed to get device info from app:', error);
      }

      // ✅ Fallback after short delay if native does NOT respond
      setTimeout(() => {
        if (!nativeResolved) {
          (UAParser().withClientHints() as PromiseLike<IResult>).then((result) => {
            // ✅ iOS simulator fix
            if (
              result?.os?.name?.toLowerCase().includes('android') &&
              navigator.platform.includes('iPhone')
            ) {
              setUserAgentInfo({
                ...result,
                os: { name: 'iOS', version: '16.0' },
                device: { vendor: 'Apple', model: 'iPhone', type: 'mobile' },
                browser: { name: 'Mobile Safari', version: result?.browser?.version || '' },
              } as IResult);
            } else {
              setUserAgentInfo(result);
            }
          });
        }
      }, 300);
    } 
    
    // ✅ Normal Web Browser
    else {
      (UAParser().withClientHints() as PromiseLike<IResult>).then((result) => {
        // Fix for Chrome DevTools device emulation
        const isDesktop = typeof window !== 'undefined' && (
          navigator.platform.includes('Win') || 
          navigator.platform.includes('Mac') || 
          navigator.platform.includes('Linux')
        );
        
        // If we're on desktop but UA parser says mobile, it's likely DevTools emulation
        if (isDesktop && result?.device?.type === 'mobile') {
          console.log('🔧 Detected desktop with mobile emulation, using actual desktop info');
          const actualResult = {
            ...result,
            device: { 
              vendor: navigator.platform.includes('Mac') ? 'Apple' : 'PC',
              model: navigator.platform.includes('Mac') ? 'Macintosh' : 'Desktop',
              type: undefined 
            },
            os: {
              name: navigator.platform.includes('Mac') ? 'macOS' : 
                    navigator.platform.includes('Win') ? 'Windows' : 'Linux',
              version: ''
            }
          };
          setUserAgentInfo(actualResult as IResult);
        } else {
          setUserAgentInfo(result);
        }
      });
    }

    // ✅ iOS Native Response Listener
    const handleDeviceInfoResponse = (event: any) => {
      try {
        const deviceInfo = event.detail || event.data;
        if (deviceInfo) {
          nativeResolved = true;
          setAppDeviceInfo(deviceInfo);
        }
      } catch (e) {
        console.warn('Failed to parse device info response:', e);
      }
    };

    document.addEventListener('deviceInfoResponse', handleDeviceInfoResponse);

    return () => {
      document.removeEventListener('deviceInfoResponse', handleDeviceInfoResponse);
    };
  }, []);
  const parser = new UAParser();
  const result: IResult = parser.getResult();
  console.log('UAParser result:', result);

  // ✅ Final Output (Safe Defaults)
  if (appDeviceInfo) {
    return {
      deviceManufacturer: appDeviceInfo.manufacturer || '',
      deviceModel: appDeviceInfo.model || '',
      deviceOS: appDeviceInfo.os || '',
      deviceBrowser: appDeviceInfo.browser || '',
    };
  }

  return {
    deviceManufacturer: userAgentInfo?.device?.vendor || '',
    deviceModel: userAgentInfo?.device?.model || '',
    deviceOS: `${userAgentInfo?.os?.name || ''} ${userAgentInfo?.os?.version || ''}`.trim(),
    deviceBrowser: `${userAgentInfo?.browser?.name || ''} ${userAgentInfo?.browser?.version || ''}`.trim(),
  };
};
